import { MenuItem as MuiMenuItem, MenuItemProps as MuiMenuItemProps, styled } from '@mui/material';
import React, { forwardRef } from 'react';

import { linkable } from '../../routing';
import { bodySmall, colorSystem } from '../../theme';
import { CircularProgress } from '../CircularProgress';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
export type MenuItemVariant = 'primary' | 'destructive';

export type MenuItemProps = MuiMenuItemProps & {
    label: string | JSX.Element;
    startIcon?: IconElement;
    variant?: MenuItemVariant;
    isLoading?: boolean;
    badge?: JSX.Element;
    overrides?: {
        InnerItem: React.ComponentType<{ label: string | JSX.Element; startIcon?: IconElement; badge?: JSX.Element }>;
    };
};

const DefaultInnerItem = ({
    label,
    startIcon,
    badge,
}: {
    label: string | JSX.Element;
    startIcon?: IconElement;
    badge?: JSX.Element;
}) => (
    <>
        {startIcon && <StartIcon>{startIcon}</StartIcon>}
        <span style={{ ...bodySmall, width: '100%' }}>{label}</span>
        {badge && <div style={{ marginLeft: 'auto' }}>{badge}</div>}
    </>
);

type MenuItemLayoutProps = Pick<MenuItemProps, 'label' | 'startIcon' | 'badge' | 'isLoading' | 'overrides'>;

/** The content both hosts render: label, start icon (a spinner while loading) and badge. */
const MenuItemLayout = ({ label, startIcon, badge, isLoading, overrides }: MenuItemLayoutProps) => {
    const InnerItem = overrides?.InnerItem ?? DefaultInnerItem;
    return (
        <InnerItem label={label} startIcon={isLoading ? <CircularProgress size="small" /> : startIcon} badge={badge} />
    );
};

const MenuItemHost = forwardRef<HTMLLIElement, MenuItemProps>(
    ({ label, startIcon, variant = 'primary', overrides, isLoading, badge, ...props }, ref) => (
        <DefaultMenuItem ref={ref} {...props} disableRipple={true} variant={variant}>
            <MenuItemLayout
                label={label}
                startIcon={startIcon}
                badge={badge}
                isLoading={isLoading}
                overrides={overrides}
            />
        </DefaultMenuItem>
    ),
);

/** The plain host's layout on an `<a>`, so the router's `href` and `onClick` reach the anchor. */
const MenuItemAnchorHost = forwardRef<HTMLLIElement, MenuItemProps>(
    ({ label, startIcon, variant = 'primary', overrides, isLoading, badge, ...props }, ref) => (
        <DefaultMenuItem ref={ref} {...props} {...{ component: 'a' }} disableRipple={true} variant={variant}>
            <MenuItemLayout
                label={label}
                startIcon={startIcon}
                badge={badge}
                isLoading={isLoading}
                overrides={overrides}
            />
        </DefaultMenuItem>
    ),
);

// To open another site: `onClick={() => window.open(url, '_blank', 'noopener')}`.
/** With `to`, a real link whose `onClick` only closes the menu; a click that also tracks calls `navigate()` instead. */
export const MenuItem = linkable(MenuItemHost, { linkHost: MenuItemAnchorHost, allowOnClick: true });

/** Sizes the start icon: call sites pass no size. */
const StartIcon = styled('span')({
    display: 'inline-flex',
    flexShrink: 0,
    marginRight: '8px',
    color: colorSystem.neutral[6],
    ...iconSlot(16),
});

const DefaultMenuItem = styled(MuiMenuItem)(({ variant }: { variant?: MenuItemVariant }) => ({
    padding: '8px',
    margin: '4px',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
        background: colorSystem.neutral[1],
        borderRadius: '4px',
        '& * > svg': {
            color: variant === 'primary' ? colorSystem.neutral[6] : colorSystem.red[7],
        },
        '& span': {
            color: variant === 'primary' ? colorSystem.neutral[9] : colorSystem.red[7],
        },
    },
}));
